import {
	AttachmentFile,
	AttachmentFileType,
	defaultAttachmentTypeLabelMap,
	ExtractionStatus,
	FileId,
	FileType,
	FinancialPeriodPreparationMethod,
	PeriodType,
} from "@inrev/common";
import { UseUpload } from "@portal/api/bond-request/agent";
import { FileUploadStatus, resolveFileUploadStatus } from "@portal/api/files";
import { DateTime } from "luxon";
import { FC, useMemo, useState } from "react";
import { accountAttachmentTypeLabelMap } from "../../constants";
import { FileUploadStatusIndicator } from "../features/file-upload";
import { Icon } from "../primitives";
import { AttachmentTypeMultiSelect } from "../ui/AttachmentTypeMultiSelect";
import { filterToAllowedAttachmentTypes } from "../ui/attachment-type-utils";
import { PeriodPreparationMethodTag } from "../ui/Financials/PeriodPreparationMethodTag";
import { Tag } from "../ui/Tag";
import { TagActionButton } from "../ui/TagActionButton";
import { UploadedDocumentRow } from "./types";

type UpdateAttachmentTypesMutateFn<TFileType extends AttachmentFileType = AttachmentFileType> =
	ReturnType<UseUpload<AttachmentFile<TFileType>[], TFileType>>["updateFn"];

type GenericDocumentTag =
	| {
			kind: "financialSummary";
			label: string;
			method?: FinancialPeriodPreparationMethod;
	  }
	| { kind: "fileType"; label: string };

const cpaBundleTypes = new Set<FileType>([
	FileType.fye_company_balance_sheet,
	FileType.fye_company_income_statement,
	FileType.fye_company_cash_flow_statement,
]);

const wipFamilyTypes = new Set<FileType>([FileType.wip, FileType.other_wip]);

type StatementAbbreviation = { period: "FYE" | "Interim"; noun: string };

const statementAbbreviationByType: Partial<Record<FileType, StatementAbbreviation>> = {
	[FileType.fye_company_balance_sheet]: { period: "FYE", noun: "BS" },
	[FileType.interim_company_balance_sheet]: { period: "Interim", noun: "BS" },
	[FileType.fye_company_income_statement]: { period: "FYE", noun: "IS" },
	[FileType.interim_company_income_statement]: { period: "Interim", noun: "IS" },
	[FileType.fye_company_cash_flow_statement]: { period: "FYE", noun: "CF" },
	[FileType.interim_company_cash_flow_statement]: { period: "Interim", noun: "CF" },
	[FileType.fye_company_ar_aging]: { period: "FYE", noun: "AR Aging" },
	[FileType.interim_company_ar_aging]: { period: "Interim", noun: "AR Aging" },
	[FileType.fye_company_ap_aging]: { period: "FYE", noun: "AP Aging" },
	[FileType.interim_company_ap_aging]: { period: "Interim", noun: "AP Aging" },
};

const statementNounRank = new Map(
	["BS", "IS", "CF", "AR Aging", "AP Aging"].map((noun, i) => [noun, i]),
);
const statementPeriodOrder: readonly StatementAbbreviation["period"][] = ["FYE", "Interim"];

type SummaryPeriod = StatementAbbreviation["period"] | null;

function groupedStatementLabels(types: FileType[], summaryPeriod: SummaryPeriod): string[] {
	const nounsByPeriod = new Map<StatementAbbreviation["period"], string[]>();
	for (const t of types) {
		const abbreviation = statementAbbreviationByType[t];
		if (!abbreviation) continue;
		const nouns = nounsByPeriod.get(abbreviation.period) ?? [];
		if (!nouns.includes(abbreviation.noun)) nouns.push(abbreviation.noun);
		nounsByPeriod.set(abbreviation.period, nouns);
	}

	const labels: string[] = [];
	for (const period of statementPeriodOrder) {
		const nouns = nounsByPeriod.get(period);
		if (!nouns || nouns.length === 0) continue;
		const body = [...nouns]
			.sort((a, b) => (statementNounRank.get(a) ?? 0) - (statementNounRank.get(b) ?? 0))
			.join(" · ");
		// The period is redundant when it matches the summary tag, so drop it there;
		// keep it when it differs so a period mismatch stays visible.
		labels.push(summaryPeriod === period ? body : `${period} ${body}`);
	}
	return labels;
}

const compactOtherLabelByType: Partial<Record<FileType, string>> = {
	[FileType.other_company_financial_statement]: "Other Corp. Financials",
	[FileType.individual_financial_statement]: "Ind. Financials",
	[FileType.individual_cash_verification]: "Ind. Cash Verification",
	[FileType.individual_credit_report]: "Ind. Credit Report",
	[FileType.other_individual_financial_statement]: "Other Ind. Financials",
};

function otherTypeLabels(
	types: FileType[],
	labelMap: Partial<Record<FileType, string>> = defaultAttachmentTypeLabelMap,
): string[] {
	const labels: string[] = [];
	const seen = new Set<string>();
	for (const t of types) {
		if (statementAbbreviationByType[t]) continue;
		const label = compactOtherLabelByType[t] ?? labelMap[t];
		if (label && !seen.has(label)) {
			seen.add(label);
			labels.push(label);
		}
	}
	return labels;
}

export function combinedGenericDocumentTags(
	row: UploadedDocumentRow,
	labelMap?: Partial<Record<FileType, string>>,
): GenericDocumentTag[] {
	const tags: GenericDocumentTag[] = [];
	const ext = row.financialExtraction;

	const pushFileTypeTags = (types: FileType[], summaryPeriod: SummaryPeriod) => {
		for (const label of groupedStatementLabels(types, summaryPeriod)) {
			tags.push({ kind: "fileType", label });
		}
		for (const label of otherTypeLabels(types, labelMap)) {
			tags.push({ kind: "fileType", label });
		}
	};

	if (!ext || ext.status !== ExtractionStatus.Completed) {
		pushFileTypeTags(row.file.types ?? [], null);
		return tags;
	}

	const dateStr = ext.periodEndDate
		? DateTime.fromISO(ext.periodEndDate).toFormat("MM/dd/yy")
		: undefined;
	const typeStr = ext.periodType ? PeriodType.displayName(ext.periodType) : undefined;
	const prepStr = ext.preparationMethod
		? FinancialPeriodPreparationMethod.displayName(ext.preparationMethod)
		: undefined;
	const label = [dateStr, typeStr, prepStr].filter(Boolean).join(" ");
	const hasSummaryTag = label.length > 0;
	if (hasSummaryTag) {
		tags.push({ kind: "financialSummary", label, method: ext.preparationMethod ?? undefined });
	}

	const summaryPeriod: SummaryPeriod =
		hasSummaryTag && ext.periodType
			? ext.periodType === PeriodType.Fye
				? "FYE"
				: "Interim"
			: null;

	const isCpaPrepared =
		ext.preparationMethod === FinancialPeriodPreparationMethod.Audit ||
		ext.preparationMethod === FinancialPeriodPreparationMethod.Review ||
		ext.preparationMethod === FinancialPeriodPreparationMethod.Compilation;

	if (isCpaPrepared) {
		if (ext.componentsPresent?.wipSchedule) {
			tags.push({ kind: "fileType", label: "WIP Schedule" });
		}
		const nonBundleTypes = (row.file.types ?? []).filter(
			(t) => !cpaBundleTypes.has(t) && !wipFamilyTypes.has(t),
		);
		pushFileTypeTags(nonBundleTypes, summaryPeriod);
	} else {
		pushFileTypeTags(row.file.types ?? [], summaryPeriod);
	}

	return tags;
}

const compactTagClass = "px-2 py-0.5 text-[11px]";

type GenericDocumentTagComponentProps = { tag: GenericDocumentTag; onClick?: () => void };

const GenericDocumentTagComponent: FC<GenericDocumentTagComponentProps> = ({ tag, onClick }) => {
	switch (tag.kind) {
		case "financialSummary":
			return tag.method ? (
				<PeriodPreparationMethodTag
					method={tag.method}
					label={tag.label}
					className={compactTagClass}
				/>
			) : (
				<Tag color="gray" border oval className={compactTagClass}>
					{tag.label}
				</Tag>
			);
		case "fileType":
			return (
				<Tag color="gray" border onClick={onClick} className={compactTagClass}>
					{tag.label}
				</Tag>
			);
	}
};

const MAX_VISIBLE_TAGS = 5;
const overflowTagClass = `${compactTagClass} font-medium`;

type UploadedDocumentsTagsCellProps<TFileType extends AttachmentFileType = AttachmentFileType> = {
	row: UploadedDocumentRow;
	updateAttachmentTypes: UpdateAttachmentTypesMutateFn<TFileType>;
	typeLabelMap?: Partial<Record<FileType, string>>;
	allowTypeConfirmation?: boolean;
};

export const UploadedDocumentsTagsCell = <
	TFileType extends AttachmentFileType = AttachmentFileType,
>({
	row,
	updateAttachmentTypes,
	typeLabelMap = accountAttachmentTypeLabelMap,
	allowTypeConfirmation = false,
}: UploadedDocumentsTagsCellProps<TFileType>) => {
	const [open, setOpen] = useState(false);
	const typeLabelMapKeys = useMemo(() => Object.keys(typeLabelMap), [typeLabelMap]);
	const fileTypes = row.file.types ?? [];
	const uploadStatus = resolveFileUploadStatus({
		classificationStatus: row.file.classificationStatus,
		hasTypes: fileTypes.length > 0,
		typesConfirmedByAdmin: row.file.typesConfirmedByAdmin,
	});
	const showAwaitingReviewActions = uploadStatus === FileUploadStatus.awaitingReview;

	const tags = useMemo(() => combinedGenericDocumentTags(row, typeLabelMap), [row, typeLabelMap]);
	const existingTypes = (row.file.types ?? []) as TFileType[];
	const openModal = () => setOpen(true);

	const handleSave = (selectedTypes: TFileType[]) => {
		updateAttachmentTypes({
			pendingFile: {
				id: row.id as unknown as FileId,
				name: row.file.name,
				extension: row.file.extension,
				types: filterToAllowedAttachmentTypes(selectedTypes, typeLabelMapKeys as TFileType[]),
				typesConfirmedByAdmin: row.file.typesConfirmedByAdmin,
			},
		});
	};

	return (
		<>
			<div className="flex items-center gap-1.5">
				<div className="flex flex-shrink-0 items-center gap-1.5">
					<FileUploadStatusIndicator status={uploadStatus} id={String(row.id)} />
					{showAwaitingReviewActions && allowTypeConfirmation && (
						<Tag
							color="green"
							border
							iconOnly
							testId="confirm-suggested-file-types"
							onClick={() => handleSave(existingTypes)}
						>
							<Icon type="check" size="mini" color="confirm" />
						</Tag>
					)}
				</div>
				<div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
					{tags.slice(0, MAX_VISIBLE_TAGS).map((tag, i) => (
						<GenericDocumentTagComponent
							key={i}
							tag={tag}
							onClick={allowTypeConfirmation ? openModal : undefined}
						/>
					))}
					{tags.length > MAX_VISIBLE_TAGS && (
						<Tag
							color="gray"
							border
							onClick={allowTypeConfirmation ? openModal : undefined}
							className={
								allowTypeConfirmation ? `${overflowTagClass} cursor-pointer` : overflowTagClass
							}
						>
							{`+${tags.length - MAX_VISIBLE_TAGS}`}
						</Tag>
					)}
					{allowTypeConfirmation && (
						<TagActionButton iconOnly={tags.length > 0} onClick={openModal} />
					)}
				</div>
			</div>
			{open && (
				<AttachmentTypeMultiSelect
					availableTypes={typeLabelMapKeys as TFileType[]}
					initialTypes={existingTypes}
					onSave={handleSave}
					expandedByDefault={false}
					title="Select File Type"
					close={() => setOpen(false)}
				/>
			)}
		</>
	);
};
